import {
  Circle,
  Hand,
  Highlighter,
  Loader2,
  Pencil,
  ScanSearch,
  Sparkles,
  Underline as UnderlineIcon,
  Wand2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  CUE_ANNOTATION,
  CUE_SPOTLIGHT,
  CUE_STROKE,
  cueForElement,
  playSfx,
  SlideStage as PlayerSlideStage,
  CameraPlaybackProvider as PlayerCameraProvider,
  type ElementCameraPlayback as PlayerElementCameraPlayback,
  type Annotation as PlayerAnnotation,
  type DrawStroke as PlayerDrawStroke,
  type Slide as PlayerSlide,
  type Spotlight as PlayerSpotlight,
  type StrokeTexture,
} from 'ai-tutor-player';

import { ScaledStage } from '@/components/canvas/ScaledStage';
import {
  DEFAULT_RETURN_TRANSITION_MS,
  selectActiveLabels,
  selectLabelFocus,
  type CameraPose,
} from 'ai-tutor-player';
import {
  CameraPlaybackProvider,
  type ElementCameraPlayback,
} from 'ai-tutor-player';
import { api } from '@/lib/api';
import { createAnnotation, createRevealStep } from '@/lib/factories';
import {
  selectVisibleAnnotations,
  selectVisibleElementIds,
  selectVisibleSpotlights,
  selectVisibleStrokes,
} from 'ai-tutor-player';
import type {
  AnnotationKind,
  DrawStroke,
  RevealStep,
  RevealStepAudio,
  Spotlight,
  ThreeDContent,
} from '@/lib/schema.types';
import { cn, uid } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

import { AiTeachDialog } from './AiTeachDialog';
import { AudioEditor } from './AudioEditor';
import { CameraKeyframeEditor } from './CameraKeyframeEditor';
import { Stage } from './Stage';
import { StepInspector } from './StepInspector';
import { TimelineDock } from './timeline/TimelineDock';
import type { ClipRef } from './timeline/clips';
import { DEFAULT_EFFECT_MS } from './timeline/items';
import { buildTimeline, clampMs, globalToStep } from './timeline/playback';

export type TeachTool = 'cursor' | AnnotationKind | 'pen' | 'highlighter' | 'spotlight';

const perfNow = () => (typeof performance !== 'undefined' ? performance.now() : 0);
const DOCK_KEY = 'teach.dock.height';
const RAIL_KEY = 'teach.rail.width';
const AUDIO_KEY = 'teach.audio.width';
const clampDock = (h: number) =>
  Math.max(window.innerHeight / 10, Math.min((window.innerHeight * 2) / 3, h));
// Side panels resize down to 1/20th of the screen and up to half of it.
const clampSide = (w: number) =>
  Math.max(window.innerWidth / 20, Math.min(window.innerWidth * 0.5, w));

interface Props {
  slideId: string;
  onClose: () => void;
}

export function TeachStudio({ slideId, onClose }: Props) {
  const slide = useEditorStore((s) => s.script?.slides.find((x) => x.id === slideId) ?? null);
  const actions = useEditorActions();
  const audioRef = useRef<HTMLAudioElement>(null);

  const steps = useMemo(() => slide?.revealSteps ?? [], [slide?.revealSteps]);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(steps[0]?.id ?? null);
  const [selectedClip, setSelectedClip] = useState<ClipRef | null>(null);
  const [tool, setTool] = useState<TeachTool>('cursor');
  const [aiOpen, setAiOpen] = useState(false);

  // ── Playback / scrub (Premiere-style): one global ms playhead across all the
  // steps (which play back-to-back). `isPreview` means the stage renders the
  // frame at the playhead (vs edit mode = show everything + tools). ──
  const { spans, total } = useMemo(() => buildTimeline(steps), [steps]);
  const [playheadMs, setPlayheadMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  // Media texture applied to NEW pen/marker strokes (undefined = smooth ink).
  const [penTexture, setPenTexture] = useState<StrokeTexture | undefined>(undefined);
  const playheadRef = useRef(0);
  const lastTsRef = useRef(0);
  const playStepRef = useRef(-1);
  const isPreviewRef = useRef(false);
  const togglePlayRef = useRef<() => void>(() => {});
  useEffect(() => {
    isPreviewRef.current = isPreview;
  }, [isPreview]);

  // Rehearsal: while the audio editor is RECORDING narration for the selected
  // step, play that step's visuals on an UNBOUNDED wall-clock — the author
  // watches handwriting/reveals complete while narrating. Without this, a
  // silent step's 3s fallback length cuts the preview off mid-write. Once the
  // recorded (or modulated) clip is attached, its durationMs becomes the real
  // step length and normal playback takes over.
  const [rehearsalStepIndex, setRehearsalStepIndex] = useState<number | null>(null);
  const [rehearsalMs, setRehearsalMs] = useState(0);
  // Ref, not state: the recorder's `onstop` fires from a closure captured at
  // record-START (stale render), so the stop path must not read state.
  const rehearsingRef = useRef(false);
  const onRecordingChange = (active: boolean) => {
    if (active) {
      const idx = steps.findIndex((s) => s.id === selectedStepId);
      if (idx < 0) return;
      rehearsingRef.current = true;
      setIsPlaying(false);
      audioRef.current?.pause();
      setRehearsalMs(0);
      setRehearsalStepIndex(idx);
      setIsPreview(true);
    } else if (rehearsingRef.current) {
      rehearsingRef.current = false;
      setRehearsalStepIndex(null);
      setIsPreview(false);
    }
  };
  useEffect(() => {
    if (rehearsalStepIndex == null) return undefined;
    let raf = 0;
    const t0 = performance.now();
    const tick = (ts: number) => {
      setRehearsalMs(ts - t0);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [rehearsalStepIndex]);

  const setPlayhead = (ms: number) => {
    const t = clampMs(ms, total);
    playheadRef.current = t;
    setPlayheadMs(t);
  };
  const exitPreview = () => {
    setIsPlaying(false);
    setIsPreview(false);
    rehearsingRef.current = false;
    setRehearsalStepIndex(null);
    audioRef.current?.pause();
  };
  const togglePlay = () => {
    if (steps.length === 0) return;
    if (rehearsalStepIndex != null) {
      rehearsingRef.current = false;
      setRehearsalStepIndex(null);
    }
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    setIsPreview(true);
    if (playheadRef.current >= total) setPlayhead(0);
    playStepRef.current = -1;
    setIsPlaying(true);
  };
  togglePlayRef.current = togglePlay;
  const scrub = (ms: number) => {
    setIsPlaying(false);
    setIsPreview(true);
    rehearsingRef.current = false;
    setRehearsalStepIndex(null);
    playStepRef.current = -1;
    audioRef.current?.pause();
    setPlayhead(ms);
  };
  const selectClip = (ref: ClipRef | null) => {
    exitPreview();
    setSelectedClip(ref);
    if (ref) setSelectedStepId(ref.stepId);
  };
  const pickTool = (t: TeachTool) => {
    exitPreview();
    setTool(t);
  };

  // Keep the selected step in sync if it gets removed.
  useEffect(() => {
    if (selectedStepId && !steps.some((s) => s.id === selectedStepId)) {
      setSelectedStepId(steps[0]?.id ?? null);
    } else if (!selectedStepId && steps.length > 0) {
      setSelectedStepId(steps[0].id);
    }
  }, [steps, selectedStepId]);

  // Resizable bottom dock (persisted), clamped to [1/10, 2/3] of the screen.
  const [dockHeight, setDockHeight] = useState(() => {
    const v = Number(localStorage.getItem(DOCK_KEY));
    return Number.isFinite(v) && v > 0 ? v : 300;
  });
  const dockResize = useRef<{ y: number; h: number } | null>(null);
  const [railWidth, setRailWidth] = useState(() => {
    const v = Number(localStorage.getItem(RAIL_KEY));
    return Number.isFinite(v) && v > 0 ? clampSide(v) : 360;
  });
  const railResize = useRef<{ x: number; w: number } | null>(null);
  const [audioWidth, setAudioWidth] = useState(() => {
    const v = Number(localStorage.getItem(AUDIO_KEY));
    return Number.isFinite(v) && v > 0 ? clampSide(v) : 360;
  });
  const audioResize = useRef<{ x: number; w: number } | null>(null);
  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (dockResize.current) {
        const h = clampDock(dockResize.current.h - (e.clientY - dockResize.current.y)); // drag up → taller
        setDockHeight(h);
        localStorage.setItem(DOCK_KEY, String(Math.round(h)));
      }
      if (railResize.current) {
        const w = clampSide(railResize.current.w + (railResize.current.x - e.clientX)); // drag left → wider
        setRailWidth(w);
        localStorage.setItem(RAIL_KEY, String(Math.round(w)));
      }
      if (audioResize.current) {
        const w = clampSide(audioResize.current.w + (e.clientX - audioResize.current.x)); // drag right → wider
        setAudioWidth(w);
        localStorage.setItem(AUDIO_KEY, String(Math.round(w)));
      }
    };
    const up = () => {
      dockResize.current = null;
      railResize.current = null;
      audioResize.current = null;
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, []);

  // Spacebar toggles play/pause (unless typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;
      e.preventDefault();
      // Blur a focused button so its synthesized space-click doesn't double-toggle.
      if (tag === 'BUTTON') t?.blur();
      togglePlayRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // While previewing/scrubbing, follow the playhead: open the current step's
  // properties in the right rail.
  useEffect(() => {
    if (!isPreview) return;
    const { index } = globalToStep(spans, clampMs(playheadMs, total));
    const id = steps[index]?.id;
    if (id && id !== selectedStepId) {
      setSelectedStepId(id);
      setSelectedClip(null);
    }
  }, [isPreview, playheadMs, spans, total, steps, selectedStepId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Esc exits preview first, else closes the studio.
      if (isPreviewRef.current) exitPreview();
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  // Seed the timeline so every element on the slide appears as a reveal step
  // (once per slide on mount). Elements removed later stay removed for the session.
  useEffect(() => {
    const state = useEditorStore.getState();
    const liveSlide = state.script?.slides.find((s) => s.id === slideId);
    if (!liveSlide) return;
    const referenced = new Set<string>();
    (liveSlide.revealSteps ?? []).forEach((s) =>
      (s.elementIds ?? []).forEach((id) => referenced.add(id)),
    );
    const missing: string[] = [];
    liveSlide.rows.forEach((row) =>
      row.columns.forEach((col) =>
        col.elements.forEach((el) => {
          if (!referenced.has(el.id)) missing.push(el.id);
        }),
      ),
    );
    if (missing.length === 0) return;
    // Element-major: don't fragment the narration with one step per element.
    // Add orphan elements to the first existing segment (revealed from its
    // start; the author re-times them by dragging each element's track), or
    // create a single segment holding them all when there's no narration yet.
    const existing = liveSlide.revealSteps ?? [];
    if (existing.length > 0) {
      const first = existing[0];
      state.upsertRevealStep(slideId, {
        ...first,
        elementIds: [...(first.elementIds ?? []), ...missing],
      });
      if (!selectedStepId) setSelectedStepId(first.id);
    } else {
      const step: RevealStep = { ...createRevealStep('Narration'), elementIds: missing };
      state.upsertRevealStep(slideId, step);
      if (!selectedStepId) setSelectedStepId(step.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideId]);

  const selectedStep: RevealStep | null = steps.find((s) => s.id === selectedStepId) ?? null;

  // Every 3D element on the slide + the "home" pose its camera returns to.
  const threeDElements = useMemo(() => {
    const out: { id: string; defaultPose: CameraPose }[] = [];
    slide?.rows.forEach((row) =>
      row.columns.forEach((col) =>
        col.elements.forEach((el) => {
          if (el.type !== '3d') return;
          const data =
            el.content && typeof el.content === 'object' ? (el.content as ThreeDContent) : null;
          out.push({
            id: el.id,
            defaultPose: {
              cameraOrbit: data?.defaultCameraOrbit ?? '0deg 75deg 105%',
              cameraTarget: data?.defaultCameraTarget ?? 'auto auto auto',
              fieldOfView: data?.fieldOfView,
            },
          });
        }),
      ),
    );
    return out;
  }, [slide]);

  // The frame to show on the stage at the current playhead (only while
  // previewing). During recording rehearsal the frame is the rehearsed step at
  // an unbounded local clock instead of the playhead (see rehearsal above).
  const frame = useMemo(() => {
    if (!isPreview || steps.length === 0) return null;
    const { index, localMs } =
      rehearsalStepIndex != null && rehearsalStepIndex < steps.length
        ? { index: rehearsalStepIndex, localMs: rehearsalMs }
        : globalToStep(spans, clampMs(playheadMs, total));
    const revealed = selectVisibleElementIds(steps, index, localMs);
    const annotations = selectVisibleAnnotations(steps, index, localMs);
    const strokes = selectVisibleStrokes(steps, index, localMs);
    const spotlights = selectVisibleSpotlights(steps, index, localMs);
    let camera: Record<string, ElementCameraPlayback> | null = null;
    if (threeDElements.length > 0) {
      const kfs = steps[index]?.cameraKeyframes ?? [];
      const map: Record<string, ElementCameraPlayback> = {};
      for (const el of threeDElements) {
        const elKfs = kfs.filter((k) => k.targetElementId === el.id);
        if (elKfs.length === 0) continue;
        const focus = selectLabelFocus(elKfs, localMs);
        const pose: CameraPose = focus
          ? focus.pose
          : {
              cameraOrbit: el.defaultPose.cameraOrbit,
              cameraTarget: 'auto auto auto',
              fieldOfView: el.defaultPose.fieldOfView,
            };
        map[el.id] = {
          pose,
          labels: selectActiveLabels(elKfs, localMs),
          transitionMs: focus ? focus.transitionMs : DEFAULT_RETURN_TRANSITION_MS,
        };
      }
      camera = map;
    }
    return { revealed, annotations, strokes, spotlights, camera };
  }, [isPreview, playheadMs, spans, total, steps, threeDElements, rehearsalStepIndex, rehearsalMs]);

  // Reveal sound cues — only during actual forward Play (not scrubbing), for
  // things newly revealed. Seeds the "already shown" sets on play-start so it
  // doesn't fire a burst for everything already on screen.
  const elementTypeMap = useMemo(() => {
    const m = new Map<string, string>();
    slide?.rows.forEach((r) => r.columns.forEach((c) => c.elements.forEach((e) => m.set(e.id, e.type))));
    return m;
  }, [slide]);
  const cuePrev = useRef({
    els: new Set<string>(),
    annos: new Set<string>(),
    spots: new Set<string>(),
    strokes: new Set<string>(),
  });
  const wasPlayingForCues = useRef(false);
  useEffect(() => {
    if (!isPlaying || !frame) {
      wasPlayingForCues.current = false;
      return;
    }
    const snap = () => ({
      els: new Set(frame.revealed),
      annos: new Set(frame.annotations.map((a) => a.id)),
      spots: new Set(frame.spotlights.map((s) => s.id)),
      strokes: new Set(frame.strokes.map((s) => s.id)),
    });
    if (!wasPlayingForCues.current) {
      wasPlayingForCues.current = true;
      cuePrev.current = snap();
      return;
    }
    const p = cuePrev.current;
    for (const id of frame.revealed) if (!p.els.has(id)) playSfx(cueForElement(elementTypeMap.get(id) ?? 'text'));
    for (const a of frame.annotations) if (!p.annos.has(a.id)) playSfx(CUE_ANNOTATION);
    for (const s of frame.spotlights) if (!p.spots.has(s.id)) playSfx(CUE_SPOTLIGHT);
    for (const s of frame.strokes) if (!p.strokes.has(s.id)) playSfx(CUE_STROKE);
    cuePrev.current = snap();
  }, [frame, isPlaying, elementTypeMap]);

  // Playback driver: advance the playhead off the audio clock (per step) or a
  // wall-clock for silent steps; reveals/camera follow via `frame` above.
  useEffect(() => {
    if (!isPlaying) return undefined;
    const a = audioRef.current;
    let raf = 0;
    lastTsRef.current = perfNow();
    playStepRef.current = -1;
    const tick = () => {
      const now = perfNow();
      let T = playheadRef.current;
      const { index } = globalToStep(spans, T);
      const span = spans[index];
      const step = steps[index];
      if (!span) {
        setIsPlaying(false);
        return;
      }
      const localMs = T - span.start;
      if (step?.audio?.src && a) {
        if (playStepRef.current !== index) {
          playStepRef.current = index;
          a.src = step.audio.src;
          a.playbackRate = step.audio.playbackSpeed ?? 1;
          try {
            a.currentTime = Math.max(0, localMs / 1000);
          } catch {
            /* not yet seekable */
          }
          void a.play().catch(() => {});
        }
        const audioLocal = (a.currentTime || 0) * 1000;
        T = a.ended || audioLocal >= span.dur - 20 ? span.start + span.dur : span.start + Math.min(audioLocal, span.dur);
      } else {
        if (a && playStepRef.current !== index) {
          a.pause();
          playStepRef.current = index;
        }
        T += now - lastTsRef.current;
      }
      lastTsRef.current = now;
      if (T >= total) {
        playheadRef.current = total;
        setPlayheadMs(total);
        setIsPlaying(false);
        a?.pause();
        return;
      }
      playheadRef.current = T;
      setPlayheadMs(T);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      audioRef.current?.pause();
    };
  }, [isPlaying, spans, total, steps]);

  // A finished pen/highlighter stroke → append to the selected step (or a new
  // "Drawing" step when none is selected).
  const onStrokeComplete = (stroke: Omit<DrawStroke, 'id'>) => {
    if (isPreview) return;
    const full: DrawStroke = { ...stroke, id: uid('stroke') };
    if (selectedStep) {
      actions.upsertRevealStep(slideId, {
        ...selectedStep,
        drawStrokes: [...(selectedStep.drawStrokes ?? []), full],
      });
    } else {
      const step: RevealStep = { ...createRevealStep('Drawing'), drawStrokes: [full] };
      actions.upsertRevealStep(slideId, step, steps.length);
      setSelectedStepId(step.id);
    }
  };

  if (!slide) return null;

  const onAddEmptyStep = () => {
    const step = createRevealStep(`Step ${steps.length + 1}`);
    actions.upsertRevealStep(slideId, step);
    setSelectedStepId(step.id);
  };

  /**
   * Click an element on the Stage with an annotation tool selected → add the
   * annotation to the SELECTED step as a timeline clip (default = whole step),
   * to be word-timed on the timeline. With no step selected, mint one.
   */
  const onPickElement = (elementId: string) => {
    if (isPreview || tool === 'cursor' || tool === 'pen' || tool === 'highlighter' || tool === 'spotlight')
      return;
    const annotation = createAnnotation(tool, elementId);
    if (selectedStep) {
      const hasEl = (selectedStep.elementIds ?? []).includes(elementId);
      actions.upsertRevealStep(slideId, {
        ...selectedStep,
        elementIds: hasEl ? selectedStep.elementIds : [...(selectedStep.elementIds ?? []), elementId],
        annotations: [...(selectedStep.annotations ?? []), annotation],
      });
      setSelectedClip({ stepId: selectedStep.id, kind: 'annotation', id: annotation.id });
    } else {
      const step: RevealStep = {
        ...createRevealStep(`${capitalize(tool)}`),
        elementIds: [elementId],
        annotations: [annotation],
      };
      actions.upsertRevealStep(slideId, step, steps.length);
      setSelectedStepId(step.id);
    }
    setTool('cursor');
  };

  const onUpdateAudio = (audio: RevealStepAudio) => {
    if (!selectedStep) return;
    actions.upsertRevealStep(slideId, { ...selectedStep, audio });
  };

  // Stage view: preview → the frame at the playhead; edit → everything + tools.
  const stageRevealed = isPreview ? frame?.revealed ?? new Set<string>() : null;
  const stageAnnotations = isPreview ? frame?.annotations ?? [] : selectedStep?.annotations ?? [];
  const stageStrokes = isPreview ? frame?.strokes ?? [] : selectedStep?.drawStrokes ?? [];
  const stageSpotlights = isPreview ? frame?.spotlights ?? [] : selectedStep?.spotlights ?? [];
  const stageCamera = isPreview ? frame?.camera ?? null : null;

  // Spotlight tool → drop a circle centered at the click in the selected step.
  const onPlaceSpotlight = (x: number, y: number) => {
    const target = selectedStep;
    if (!target) return;
    const D = 300;
    const clamp = (v: number, max: number) => Math.max(0, Math.min(max - D, v - D / 2));
    const sp: Spotlight = {
      id: uid('sp'),
      shape: 'circle',
      x: clamp(x, 1280),
      y: clamp(y, 720),
      w: D,
      h: D,
      zoom: 1.25,
      easeMs: 700,
      triggerMs: 0,
      endMs: DEFAULT_EFFECT_MS,
    };
    actions.upsertRevealStep(slideId, { ...target, spotlights: [...(target.spotlights ?? []), sp] });
    setSelectedClip({ stepId: target.id, kind: 'spotlight', id: sp.id });
    setTool('cursor');
  };

  const onUpdateSpotlight = (id: string, patch: Partial<Spotlight>) => {
    const target = steps.find((s) => (s.spotlights ?? []).some((sp) => sp.id === id));
    if (!target) return;
    actions.upsertRevealStep(slideId, {
      ...target,
      spotlights: (target.spotlights ?? []).map((sp) => (sp.id === id ? { ...sp, ...patch } : sp)),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg">
      <Header slideLabel={slide.label} onClose={onClose} onAiTeach={() => setAiOpen(true)} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex flex-1 overflow-hidden">
          {/* Left rail — audio editor (drag the right edge to resize). */}
          <aside
            className="relative flex shrink-0 flex-col overflow-hidden border-r border-border-subtle bg-white"
            style={{ width: audioWidth }}
          >
            <div className="min-h-0 flex-1 overflow-hidden">
              <AudioEditor
                step={selectedStep}
                slide={slide}
                onUpdateAudio={onUpdateAudio}
                onRecordingChange={onRecordingChange}
              />
            </div>
            <div
              onPointerDown={(e) => {
                audioResize.current = { x: e.clientX, w: audioWidth };
                document.body.style.userSelect = 'none';
              }}
              className="group absolute -right-1 top-0 z-10 h-full w-2 cursor-ew-resize"
              title="Drag to resize the panel"
            >
              <span className="absolute left-1/2 top-1/2 h-10 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-border-strong/70 transition group-hover:bg-accent" />
            </div>
          </aside>

          {/* Center — stage (doubles as the program monitor while previewing) */}
          <main className="relative flex flex-1 flex-col bg-[#f4f5fb]">
            <ToolbarStrip
              tool={tool}
              onChange={pickTool}
              disabled={isPreview}
              penTexture={penTexture}
              onPenTexture={setPenTexture}
            />

            {/* Slide scales to fit whatever room the dock + side rail leave.
                PLAYBACK renders through the shared player's SlideStage — the
                exact renderer the LMS Preview and the Electron classroom use —
                fed by the same per-frame state the playhead computes. Edit mode
                keeps the interactive admin Stage (tools, outlines, drag). */}
            <div className="min-h-0 flex-1 overflow-hidden p-6">
              <ScaledStage fit>
                {isPreview ? (
                  <PlayerCameraProvider
                    value={stageCamera as Record<string, PlayerElementCameraPlayback> | null}
                  >
                    <PlayerSlideStage
                      slide={slide as unknown as PlayerSlide}
                      revealedElementIds={stageRevealed ?? new Set<string>()}
                      annotations={stageAnnotations as PlayerAnnotation[]}
                      drawStrokes={stageStrokes as PlayerDrawStroke[]}
                      spotlights={stageSpotlights as PlayerSpotlight[]}
                      paused={!isPlaying && rehearsalStepIndex == null}
                    />
                  </PlayerCameraProvider>
                ) : (
                  <CameraPlaybackProvider value={stageCamera}>
                    <Stage
                      slide={slide}
                      annotations={stageAnnotations}
                      drawStrokes={stageStrokes}
                      revealedElementIds={stageRevealed}
                      tool={tool}
                      onPickElement={onPickElement}
                      onStrokeComplete={onStrokeComplete}
                      penTexture={penTexture}
                      spotlights={stageSpotlights}
                      previewing={false}
                      onPlaceSpotlight={onPlaceSpotlight}
                      onUpdateSpotlight={onUpdateSpotlight}
                    />
                  </CameraPlaybackProvider>
                )}
              </ScaledStage>
            </div>

            {/* Hidden audio element the playback driver mounts clips into. */}
            <audio ref={audioRef} className="hidden" />

            {/* Offer to (re)generate the selected step's audio with word timing
                when it's missing — that's what powers the word ruler. */}
            {!isPreview && selectedStep && (
              <RegenerateAudioChip slideId={slideId} step={selectedStep} />
            )}
          </main>

          {/* Right rail — step inspector + camera (drag the left edge to resize;
              the slide rescales to the remaining space). */}
          <aside
            className="relative flex shrink-0 flex-col border-l border-border-subtle bg-white"
            style={{ width: railWidth }}
          >
            <div
              onPointerDown={(e) => {
                railResize.current = { x: e.clientX, w: railWidth };
                document.body.style.userSelect = 'none';
              }}
              className="group absolute -left-1 top-0 z-10 h-full w-2 cursor-ew-resize"
              title="Drag to resize the panel"
            >
              <span className="absolute left-1/2 top-1/2 h-10 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-border-strong/70 transition group-hover:bg-accent" />
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-2 scroll-thin">
              <StepInspector
                slideId={slideId}
                step={selectedStep}
                selectedClip={selectedClip}
                onSelectClip={selectClip}
              />
              <CameraKeyframeEditor slideId={slideId} step={selectedStep} />
            </div>
          </aside>
        </div>

        {/* Bottom rail — element-major timeline + seekbar (mini editor). Drag the
            top edge to resize (persisted, clamped to 1/10–2/3 of the screen). */}
        <div
          className="relative shrink-0 border-t border-border bg-white"
          style={{ height: clampDock(dockHeight) }}
        >
          <div
            onPointerDown={(e) => {
              dockResize.current = { y: e.clientY, h: clampDock(dockHeight) };
              document.body.style.userSelect = 'none';
            }}
            className="group absolute -top-1 left-0 right-0 z-10 flex h-2 cursor-ns-resize items-center justify-center"
            title="Drag to resize the timeline"
          >
            <span className="h-1 w-10 rounded-full bg-border-strong/70 transition group-hover:bg-accent" />
          </div>
          <TimelineDock
            slide={slide}
            steps={steps}
            slideId={slideId}
            selectedStepId={selectedStepId}
            selectedClip={selectedClip}
            playheadMs={playheadMs}
            totalMs={total}
            isPlaying={isPlaying}
            onTogglePlay={togglePlay}
            onScrub={scrub}
            onSelectStep={(id) => {
              exitPreview();
              setSelectedClip(null);
              setSelectedStepId(id);
            }}
            onSelectClip={selectClip}
            onAddEmpty={onAddEmptyStep}
            onReorder={(ids) => actions.reorderRevealSteps(slideId, ids)}
            onDeleteStep={(id) => actions.removeRevealStep(slideId, id)}
          />
        </div>
      </div>

      <AiTeachDialog open={aiOpen} onClose={() => setAiOpen(false)} slide={slide} slideId={slideId} />
    </div>
  );
}

function Header({
  slideLabel,
  onClose,
  onAiTeach,
}: {
  slideLabel: string;
  onClose: () => void;
  onAiTeach: () => void;
}) {
  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-white px-5">
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-fg">Teach Mode</span>
        <span className="text-fg-subtle">·</span>
        <span className="text-sm text-fg-muted">{slideLabel || 'Untitled slide'}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onAiTeach}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-[12px] font-medium text-fg hover:bg-bg-subtle"
        >
          <Sparkles size={13} /> AI Teach
        </button>
        <button
          onClick={onClose}
          className="grid h-8 w-8 place-items-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
          aria-label="Close teach mode"
        >
          <X size={15} />
        </button>
      </div>
    </header>
  );
}

const TOOLS: { id: TeachTool; label: string; icon: React.ReactNode }[] = [
  { id: 'encircle', label: 'Encircle', icon: <Circle size={13} /> },
  { id: 'underline', label: 'Underline', icon: <UnderlineIcon size={13} /> },
  { id: 'highlight', label: 'Highlight', icon: <Highlighter size={13} /> },
  { id: 'pen', label: 'Pen', icon: <Pencil size={13} /> },
  { id: 'highlighter', label: 'Marker', icon: <Highlighter size={13} /> },
  { id: 'spotlight', label: 'Spotlight', icon: <ScanSearch size={13} /> },
];

const PEN_TEXTURES: { id: StrokeTexture | undefined; label: string }[] = [
  { id: undefined, label: 'Ink' },
  { id: 'crayon', label: 'Crayon' },
  { id: 'chalk', label: 'Chalk' },
];

function ToolbarStrip({
  tool,
  onChange,
  disabled,
  penTexture,
  onPenTexture,
}: {
  tool: TeachTool;
  onChange: (t: TeachTool) => void;
  disabled?: boolean;
  penTexture?: StrokeTexture;
  onPenTexture?: (t: StrokeTexture | undefined) => void;
}) {
  const drawing = tool === 'pen' || tool === 'highlighter';
  return (
    <div className="flex items-center justify-center pt-6">
      <div className="flex items-center gap-1 rounded-full border border-border bg-white px-2 py-1.5 shadow-pill">
        <button
          onClick={() => onChange('cursor')}
          className={cn(
            'grid h-7 w-7 place-items-center rounded-full text-fg-muted transition',
            tool === 'cursor' && !disabled ? 'bg-bg-subtle text-fg' : 'hover:text-fg',
            disabled && 'opacity-40',
          )}
          title="Cursor"
          disabled={disabled}
        >
          <Hand size={13} />
        </button>
        <span className="h-5 w-px bg-border" />
        {TOOLS.map((t) => {
          const active = tool === t.id && !disabled;
          return (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              disabled={disabled}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition',
                active ? 'bg-accent-soft text-accent' : 'text-fg-muted hover:bg-bg-subtle hover:text-fg',
                disabled && 'opacity-40',
              )}
            >
              {t.icon}
              {t.label}
            </button>
          );
        })}
        {/* Media texture for new pen/marker strokes — only relevant while a
            drawing tool is armed. */}
        {drawing && !disabled && onPenTexture && (
          <>
            <span className="h-5 w-px bg-border" />
            {PEN_TEXTURES.map((t) => (
              <button
                key={t.label}
                onClick={() => onPenTexture(t.id)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-wider transition',
                  penTexture === t.id
                    ? 'bg-fg text-white'
                    : 'text-fg-muted hover:bg-bg-subtle hover:text-fg',
                )}
                title={`${t.label} stroke texture`}
              >
                {t.label}
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Floating chip (bottom-left) that (re)generates the selected step's audio via
 * the align TTS (RunPod VoxCPM2) so it gains per-word timestamps. Shown only
 * when the step has narration text but no `words` yet — the steps that render as
 * a flat block with no word ruler. On success the step's audio gains `words`
 * (+ a refreshed clip/duration) and the timeline lights up.
 */
function RegenerateAudioChip({ slideId, step }: { slideId: string; step: RevealStep }) {
  const actions = useEditorActions();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const text = step.audio?.text?.trim();
  const hasWords = (step.audio?.words?.length ?? 0) > 0;
  if (!text || hasWords) return null;

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.ai.generateNarration({
        text,
        voiceModel: step.audio?.voiceModel,
        playbackSpeed: step.audio?.playbackSpeed ?? 1,
        engine: 'voxcpm',
      });
      actions.upsertRevealStep(slideId, {
        ...step,
        audio: {
          ...step.audio,
          source: 'tts',
          src: res.src,
          text: res.text,
          devnagariText: res.devnagariText,
          audioForText: res.text,
          durationMs: res.durationMs,
          voiceModel: res.voiceModel,
          playbackSpeed: res.playbackSpeed,
          words: res.words,
        },
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="absolute bottom-6 left-6 max-w-[280px]">
      <button
        onClick={run}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-fg/90 px-3.5 py-2 text-[12px] font-medium text-white shadow-floating transition hover:bg-fg disabled:opacity-60"
        title="Generate audio with per-word timing (VoxCPM2 align)"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
        {busy ? 'Aligning…' : step.audio?.src ? 'Re-align audio (word timing)' : 'Generate aligned audio'}
      </button>
      {err && <div className="mt-1 rounded-md bg-red-600/90 px-2 py-1 text-[11px] text-white">{err}</div>}
    </div>
  );
}
